/**
 * default-value — format wartości domyślnej pola (tekst w kolumnie `default_value` definicji pola)
 * i jego odczyt względem dziedziny pola (`FieldValueDomain` z `core-field-domains`).
 *
 * Format:
 *  - tekst — dowolny niepusty tekst,
 *  - liczba — zapis z kropką (`12`, `-3.5`); pole całkowite — bez części ułamkowej,
 *  - tak/nie — `true` / `false` (brak wartości domyślnej = brak wpisu, osobno od `false`),
 *  - data — `YYYY-MM-DD` (pole z godziną także `YYYY-MM-DDTHH:mm`) albo względnie od dnia otwarcia
 *    formularza: `@today`, `@today+N`, `@today-N` (N dni),
 *  - wybór jednokrotny — kod opcji / pozycji słownika / id kategorii,
 *  - wybór wielokrotny — tablica JSON kodów (`["a","b"]`).
 *
 * `parseDefaultValue` sprawdza format oraz listy zamknięte (kody pól systemowych, opcje wyboru
 * pól dodatkowych). Przynależność do słownika i istnienie kategorii sprawdza serwer (wymaga bazy).
 * `resolveDefaultValue` zamienia wartość na wartość formularza — „dziś” liczy przeglądarka.
 */

import type { FieldValueDomain } from './core-field-domains';

const RELATIVE_DATE = /^@today(?:([+-])(\d{1,4}))?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const NUMBER = /^-?\d+(?:\.\d+)?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type ParsedDefaultValue =
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'fixedDate'; value: string }
  | { kind: 'relativeDate'; offsetDays: number }
  | { kind: 'choice'; multiple: false; value: string }
  | { kind: 'choices'; multiple: true; values: string[] };

/** Powód odrzucenia wartości (kod w odpowiedzi trasy, `reason`). */
export type DefaultValueFormatError =
  | 'EMPTY'
  | 'NOT_A_NUMBER'
  | 'NOT_AN_INTEGER'
  | 'NOT_A_BOOLEAN'
  | 'NOT_A_DATE'
  | 'NOT_A_LIST'
  | 'NOT_A_CATEGORY_ID'
  | 'OUT_OF_DOMAIN';

export type DefaultValueParseResult =
  | { ok: true; value: ParsedDefaultValue }
  | { ok: false; reason: DefaultValueFormatError };

const fail = (reason: DefaultValueFormatError): DefaultValueParseResult => ({ ok: false, reason });
const ok = (value: ParsedDefaultValue): DefaultValueParseResult => ({ ok: true, value });

function isRealDate(year: string, month: string, day: string): boolean {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  );
}

function parseDate(raw: string, withTime: boolean): DefaultValueParseResult {
  const relative = RELATIVE_DATE.exec(raw);
  if (relative) {
    const n = relative[2] === undefined ? 0 : Number(relative[2]);
    return ok({ kind: 'relativeDate', offsetDays: relative[1] === '-' ? -n : n });
  }
  const date = ISO_DATE.exec(raw);
  if (date) {
    return isRealDate(date[1]!, date[2]!, date[3]!)
      ? ok({ kind: 'fixedDate', value: raw })
      : fail('NOT_A_DATE');
  }
  const dateTime = withTime ? ISO_DATETIME.exec(raw) : null;
  if (dateTime) {
    const valid =
      isRealDate(dateTime[1]!, dateTime[2]!, dateTime[3]!) &&
      Number(dateTime[4]) < 24 &&
      Number(dateTime[5]) < 60;
    return valid ? ok({ kind: 'fixedDate', value: raw }) : fail('NOT_A_DATE');
  }
  return fail('NOT_A_DATE');
}

/** Kody wartości wyboru: pojedynczy kod albo tablica JSON niepustych, różnych kodów. */
function parseChoice(raw: string, multiple: boolean): DefaultValueParseResult {
  if (!multiple) return ok({ kind: 'choice', multiple: false, value: raw });
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fail('NOT_A_LIST');
  }
  if (
    !Array.isArray(parsed) ||
    parsed.length === 0 ||
    !parsed.every((v): v is string => typeof v === 'string' && v.trim() !== '') ||
    new Set(parsed).size !== parsed.length
  ) {
    return fail('NOT_A_LIST');
  }
  return ok({ kind: 'choices', multiple: true, values: parsed });
}

function choiceValues(value: ParsedDefaultValue): string[] {
  if (value.kind === 'choice') return [value.value];
  if (value.kind === 'choices') return value.values;
  return [];
}

/** Odczyt i sprawdzenie wartości względem dziedziny pola (format + listy zamknięte). */
export function parseDefaultValue(raw: string, domain: FieldValueDomain): DefaultValueParseResult {
  if (raw.trim() === '') return fail('EMPTY');
  switch (domain.kind) {
    case 'text':
      return ok({ kind: 'text', value: raw });
    case 'number': {
      if (!NUMBER.test(raw)) return fail('NOT_A_NUMBER');
      if (domain.integer && raw.includes('.')) return fail('NOT_AN_INTEGER');
      return ok({ kind: 'number', value: Number(raw) });
    }
    case 'boolean':
      if (raw === 'true') return ok({ kind: 'boolean', value: true });
      if (raw === 'false') return ok({ kind: 'boolean', value: false });
      return fail('NOT_A_BOOLEAN');
    case 'date':
      return parseDate(raw, domain.withTime);
    case 'codes':
    case 'options': {
      const result = parseChoice(raw, domain.multiple);
      if (!result.ok) return result;
      const allowed = new Set(
        domain.kind === 'codes' ? domain.codes : domain.options.map((o) => o.value),
      );
      return choiceValues(result.value).every((v) => allowed.has(v))
        ? result
        : fail('OUT_OF_DOMAIN');
    }
    case 'dictionary':
      return parseChoice(raw, domain.multiple);
    case 'category':
      return UUID.test(raw)
        ? ok({ kind: 'choice', multiple: false, value: raw })
        : fail('NOT_A_CATEGORY_ID');
  }
}

/** Kody wyboru wartości (dla sprawdzenia słownika na serwerze); dla innych typów — pusta lista. */
export function defaultValueChoiceCodes(value: ParsedDefaultValue): string[] {
  return choiceValues(value);
}

/** Wartość formularza z wartości domyślnej. */
export type ResolvedDefaultValue = string | number | boolean | string[];

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Zamienia wartość domyślną na wartość pola formularza. Daty względne liczone od `now` w strefie
 * czasu przeglądarki: pole datowe → `YYYY-MM-DD`, pole z godziną → `YYYY-MM-DDTHH:mm` (bieżąca
 * godzina przesunięta o N dni).
 */
export function resolveDefaultValue(
  value: ParsedDefaultValue,
  now: Date,
  withTime = false,
): ResolvedDefaultValue {
  switch (value.kind) {
    case 'text':
    case 'number':
    case 'boolean':
      return value.value;
    case 'fixedDate':
      // Pole z godziną przyjmuje tylko `YYYY-MM-DDTHH:mm` — sama data oznacza początek dnia.
      return withTime && !value.value.includes('T') ? `${value.value}T00:00` : value.value;
    case 'choice':
      return value.value;
    case 'choices':
      return value.values;
    case 'relativeDate': {
      const date = new Date(now);
      date.setDate(date.getDate() + value.offsetDays);
      const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      return withTime ? `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
    }
  }
}
